'use client';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { getCId } from '@coverright/utils/CId';

interface IAbTestContext {
  track: (variantKey: string) => Promise<void>;
  tests: ABTest[];
  userVariants: {testKey: string, userVariant: string | undefined}[]
}

export const AbTestContext = React.createContext<IAbTestContext>({
  track: async () => undefined,
  tests: [],
  userVariants: []
});

export function AbTestContextProvider({children}: React.PropsWithChildren) {
  const [tests, setTests] = useState<ABTest[]>([]);
  const [userVariants, setUserVariants] = useState<{testKey: string, userVariant: string | undefined}[]>([]);
  const initialRequestSent = useRef(false);

  useEffect(() => {
    if (!initialRequestSent.current) {
      getActiveTests().then(setTests)
      initialRequestSent.current = true;
    }
  }, []);

  useEffect(() => {
    if (tests) {
      setUserVariants(tests.map(test => ({
        testKey: test.key, userVariant: test.userVariant
      })))
    }
  }, [tests]);

  const track = async (variantKey: string) => {
    if (!getCId()) {
      return
    }

    const headers: RequestInit = {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        variant_key: variantKey,
        c_id: getCId(),
        url: document.location.origin + document.location.pathname,
      })
    };
    const url = new URL('https://ab.coverright.com/api/track');

    try {
      const response = await fetch(url, headers);
      return response.json();
    } catch (error) {
      console.error(error);
    }
  };

  return <AbTestContext.Provider value={{tests, track, userVariants}}>
    {children}
  </AbTestContext.Provider>
}

export const useAbTestContext = () => React.useContext(AbTestContext);

export function withABTest<P extends object>(WrappedComponent: React.FC<P>, testKey: string, variantKey: string): React.FC<P> {
  return (props: P) => {
    const {userVariants} = useAbTestContext();
    const variant = userVariants.find(v => v.testKey === testKey)?.userVariant;

    if (!userVariants.length || variant && variant !== variantKey) {
      return null;
    }
    return <WrappedComponent {...props} />
  }
}

const getActiveTests = async () => {
  const headers: RequestInit = {
    method: 'GET',
    headers: {
      "Content-Type": "application/json",
    },
    cache: "no-cache"
  };
  const url = new URL('https://ab.coverright.com/api/active-tests');

  if (getCId()) {
    url.searchParams.set('c_id', getCId()!);
  }

  try {
    const response = await fetch(url, headers);
    const json = await response.json();
    if (json.data) {
      return json.data.map((item: any) => ({
        ...item,
        startDate: item.startDate ? new Date(item.startDate) : item.startDate,
        endDate: item.endDate ? new Date(item.endDate) : item.endDate,
      }));
    }
  } catch (error) {
    console.error(error);
    return [];
  }
}

export interface ABTest {
  id: number;
  documentId: string;
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  active: boolean;
  key: string;
  userVariant: string;
  variants: ABTestVariant[];
}

export interface ABTestVariant {
  id: number;
  documentId: string;
  traffic_allocation: number | null;
  key: string;
}
